import * as Post from '@/redux/post/post.d';
import * as Feed from '@/redux/feed/feed.d';
import * as Attachment from '@/redux/attachment/attachment.d';
import { IProjectEditorState } from './post.slice';
import { IControllerType } from '@/redux/post/post.d';
import { CONTROLLER_ORDER } from '@/lib/controllerConfig';
import { getAttachmentController } from './post.slice.helpers';
import { controllerAgnosticName } from '@/pages/UploadV2/utils/fileHelpers';
import { pairsOneForOne, sortInPlace } from '@/pages/UploadV2/utils/cutOrder';
import {
  kindOf,
  SPINDLE_SPEED_MIN,
  SPINDLE_SPEED_MAX,
  MODEL_FILE_EXTENSIONS,
} from './post.constants';
import type { IBitPublic } from '@/redux/bit/bit.d';

export {
  getControllerFromFileName,
  getGroupedOptionsFlat,
} from './post.slice.helpers';

/**
 * Resolves display specs for a tool, preferring the resolved bit catalog entry
 * (if present) and falling back to the tool's own inline specs for un-backfilled
 * legacy posts. `spindleSpeed` and `laserOrEndmill` are always per-job inline.
 */
// resolveToolSpecs: trivial null-coalescing mapping; cyclomatic inflated by the ?? chain
// fallow-ignore-next-line complexity
export const resolveToolSpecs = (tool: Post.IToolData, bit?: IBitPublic) => ({
  toolType: bit?.bitType ?? tool.toolType,
  toolUsed: bit?.toolUsed ?? tool.toolUsed,
  toolUnit: bit?.toolUnit ?? tool.toolUnit,
  vBitDegree: bit?.vBitDegree ?? tool.vBitDegree,
  tipRadius: bit?.tipRadius ?? tool.tipRadius,
  // Always per-job inline — never from the catalog bit:
  spindleSpeed: tool.spindleSpeed,
  laserOrEndmill: tool.laserOrEndmill,
});

/** True when `fileName` is a CNC model/toolpath file (by extension). */
export function isModelFile(fileName: string | undefined): boolean {
  if (!fileName) return false;
  const lower = fileName.toLowerCase();
  return MODEL_FILE_EXTENSIONS.includes(lower.slice(lower.lastIndexOf('.')));
}

const modelKey = (attachment: Post.IPostAttachment) =>
  controllerAgnosticName(attachment.fileName);

/** Controllers whose files pair one for one with the first block's. */
function alignedControllers(models: Post.IPostAttachment[]) {
  const blocks = new Map<string, string[]>();
  models.forEach((attachment) => {
    const controller = getAttachmentController(attachment);
    blocks.set(controller, [
      ...(blocks.get(controller) ?? []),
      modelKey(attachment),
    ]);
  });
  const [reference = []] = blocks.values();
  return new Set(
    [...blocks]
      .filter(([, keys]) => pairsOneForOne(keys, reference))
      .map(([controller]) => controller)
  );
}

/**
 * Redline, then Masso, then Buildbotics. Blocks whose files pair one for one
 * line up cut by cut, where each cut's first file landed; any other block
 * keeps its upload order rather than sinking an unpaired file to the end.
 */
function byControllerThenCut(attachments: Post.IPostAttachment[]) {
  const models = attachments.filter((a) => isModelFile(a.fileName));
  const cuts = new Map<string, number>();
  models.forEach((attachment) => {
    const key = modelKey(attachment);
    if (!cuts.has(key)) cuts.set(key, cuts.size);
  });
  const aligned = alignedControllers(models);
  const position = new Map(models.map((a, i) => [a, i]));

  const cutOf = (attachment: Post.IPostAttachment) =>
    aligned.has(getAttachmentController(attachment))
      ? (cuts.get(modelKey(attachment)) ?? 0)
      : (position.get(attachment) ?? 0);
  const controllerOf = (attachment: Post.IPostAttachment) => {
    const rank = CONTROLLER_ORDER.indexOf(getAttachmentController(attachment));
    return rank < 0 ? CONTROLLER_ORDER.length : rank;
  };

  return (a: Post.IPostAttachment, b: Post.IPostAttachment) =>
    controllerOf(a) - controllerOf(b) || cutOf(a) - cutOf(b);
}

export function filterAttachments(
  attachments: Post.IPostAttachment[] | undefined
) {
  if (!attachments) return [];

  const listedElsewhere = new Set(
    attachments
      .filter((a) => a.category !== Post.IAttachmentCategory.postThumbnail)
      .map((a) => a.fileName)
  );

  const listed = attachments.filter((attachment) => {
    // The Redline HMI bundle is generated for the controller, not for humans —
    // it duplicates files already listed individually, so never show it.
    if (attachment.category === Post.IAttachmentCategory.redlineHmiProjectZip) {
      return false;
    }
    // Drop the thumbnail only when it repeats a row; on some posts it is the only image
    if (
      attachment.category === Post.IAttachmentCategory.postThumbnail &&
      listedElsewhere.has(attachment.fileName)
    ) {
      return false;
    }
    if (
      (attachment.resourceType === Attachment.AttachmentType.zip &&
        attachment.category === Post.IAttachmentCategory.postZip) ||
      (attachment.resourceType === Attachment.AttachmentType.youtube &&
        attachment.category === Post.IAttachmentCategory.postMedia)
    ) {
      return false;
    }
    return true;
  });

  sortInPlace(
    listed,
    (attachment) => isModelFile(attachment.fileName),
    byControllerThenCut(listed)
  );
  return listed;
}

const carouselSupportedExtensions = [
  '.jpg',
  '.jpeg',
  '.png',
  '.webp',
  '.svg',
  ...MODEL_FILE_EXTENSIONS,
];

function extOf(fileName: string | undefined): string {
  return (fileName?.split('.').pop() ?? '').toLowerCase();
}

/** Slide order, matching the upload wizard's sections: photos, then G-code,
 * then design files. The thumbnail leads and the video sits with the photos —
 * neither has a counterpart in the wizard, which marks its thumbnail in place
 * and collects no video at all. */
const SLIDE_RANK: Record<ReturnType<typeof kindOf>, number> = {
  image: 1,
  gcode: 3,
  doc: 4,
};

export function filterAttachmentsForCarousel(
  attachments: Post.IPostAttachment[] | undefined
) {
  if (!attachments) return [];

  const shown = attachments.filter((attachment) => {
    // Include YouTube attachments (they don't have file extensions)
    if (attachment.resourceType === Attachment.AttachmentType.youtube) {
      return true;
    }

    // For other attachments, check file extension
    const ext = attachment.fileName
      ?.toLowerCase()
      ?.slice(attachment.fileName.lastIndexOf('.'));
    return carouselSupportedExtensions.includes(ext);
  });

  // A postThumbnail is a copy of an image already in postMedia, and replacing
  // one leaves the previous row behind pointing at a deleted S3 object.
  const twins = (a: Post.IPostAttachment, b: Post.IPostAttachment) =>
    (Boolean(a.fileHash) && a.fileHash === b.fileHash) ||
    a.fileName === b.fileName;

  const listed = shown.filter(
    (attachment) =>
      attachment.category !== Post.IAttachmentCategory.postThumbnail ||
      !shown.some(
        (other) =>
          other.category !== Post.IAttachmentCategory.postThumbnail &&
          twins(attachment, other)
      )
  );

  // The thumbnail may have been collapsed into its postMedia twin above, and
  // that twin is what has to lead.
  const thumbnail = attachments.find(
    (attachment) =>
      attachment.category === Post.IAttachmentCategory.postThumbnail
  );
  const rank = (attachment: Post.IPostAttachment): number => {
    if (thumbnail && twins(attachment, thumbnail)) return 0;
    if (attachment.resourceType === Attachment.AttachmentType.youtube) return 2;
    return SLIDE_RANK[kindOf(extOf(attachment.fileName))];
  };

  // Stable, so the server's order still decides within a rank.
  return listed.sort((a, b) => rank(a) - rank(b));
}

/** Where the video sits in the carousel, or -1. Callers pass the same array the
 * carousel renders, so the badge can never point at a slide that is not there. */
export function videoSlideIndex(
  attachments: Post.IPostAttachment[] | undefined
): number {
  if (!attachments) return -1;
  return attachments.findIndex(
    (attachment) =>
      attachment.resourceType === Attachment.AttachmentType.youtube
  );
}

/**
 * Filters attachments by controller type
 */
export function filterAttachmentsByController(
  attachments: Post.IPostAttachment[] | undefined,
  controllerType: IControllerType
): Post.IPostAttachment[] {
  if (!attachments) return [];

  return attachments.filter((attachment) => {
    // Non-model files (e.g. images) are shown for all controllers
    if (!isModelFile(attachment.fileName)) {
      return true;
    }

    // For model files, check if they match the selected controller
    const attachmentController = getAttachmentController(attachment);
    return attachmentController === controllerType;
  });
}

/**
 * Gets the list of available controllers from attachments
 * Returns controllers in the order defined by CONTROLLER_ORDER
 */
export function getAvailableControllers(
  attachments: Post.IPostAttachment[] | undefined
): IControllerType[] {
  if (!attachments) return [];

  const controllersSet = new Set<IControllerType>();

  attachments.forEach((attachment) => {
    if (isModelFile(attachment.fileName)) {
      const controller = getAttachmentController(attachment);
      controllersSet.add(controller);
    }
  });

  // Return controllers in the defined order
  return CONTROLLER_ORDER.filter((controller) =>
    controllersSet.has(controller)
  );
}

export const getGroupedOptionsFromEnumAsGroups = (
  groupedEnum: Post.GroupedEnum
): Post.IGroupedOption[] => {
  const groupedOptions: Post.IGroupedOption[] = Object.entries(groupedEnum).map(
    ([materialGroupKey, materialEnum]) => {
      const options: Post.IOption[] = Object.entries(
        materialEnum as Post.EnumType
      ).map(([key, value]) => ({
        value: key,
        label: value,
      }));
      if (materialGroupKey === '[object Object]')
        return { label: '', options: [] };

      return {
        label:
          materialGroupKey.charAt(0).toUpperCase() +
          materialGroupKey.slice(1).toLowerCase(),
        options,
      };
    }
  );

  return groupedOptions;
};

async function getFilesByCategory(
  attachments: Post.IPostAttachment[],
  category: Post.IAttachmentCategory,
  includeYoutube: boolean = false
) {
  const filteredAttachments = attachments.filter(
    (attachment) =>
      attachment.category === category &&
      (includeYoutube ||
        attachment.resourceType !== Attachment.AttachmentType.youtube)
  );

  return filteredAttachments;
}

/**
 * Converts the response from the backend to a PostUploadData
 */
export async function convertToPostUploadData(
  dto: Post.IPostDetailResponse
): Promise<Post.IPostUploadData> {
  const thumbnailFile = await getFilesByCategory(
    dto.attachments,
    Post.IAttachmentCategory.postThumbnail
  );

  const mediaFiles = await getFilesByCategory(
    dto.attachments,
    Post.IAttachmentCategory.postMedia,
    true // Include YouTube attachments
  );

  const modelAttachments = dto.attachments.filter((attachment) => {
    if (
      attachment.category !== Post.IAttachmentCategory.postModel &&
      attachment.resourceType !== Attachment.AttachmentType.model
    )
      return false;

    return true;
  });

  const youtubeAttachments = dto.attachments
    .filter((attachment) => {
      if (
        attachment.category === Post.IAttachmentCategory.postMedia &&
        attachment.resourceType === Attachment.AttachmentType.youtube
      ) {
        return true;
      }

      return false;
    })
    .map((attachment) => attachment.resourcePath);

  const isRotary = dto.jobType === Post.IJobType.ROTARY;

  if (isRotary) {
    return {
      _id: dto._id,
      title: dto.title,
      description: dto.description,
      category: dto.category?._id || '',
      tags: dto.tags.map((tag) => tag._id),
      onefinityModel: dto.onefinityModel as Post.IOnefinityModel,
      onefinityController: dto.onefinityController as Post.IOnefinityController,
      onefinitySize: dto.onefinitySize as Post.IOnefinitySize,
      softwareUsed: (Array.isArray(dto.softwareUsed)
        ? dto.softwareUsed
        : [dto.softwareUsed].filter(Boolean)) as Post.ISoftwareUsed[],
      materialUsed: dto.materialUsed as unknown as Post.IMaterialUsed,
      difficulty: dto.difficulty ?? '',
      jobType: dto.jobType as Post.IJobType,
      units: dto.units as Post.IUnits,
      jobSizeDiameter:
        dto.jobSize?.diameter !== undefined ? dto.jobSize.diameter : 0.1,
      jobSizeLength:
        dto.jobSize?.length !== undefined ? dto.jobSize.length : 0.1,
      zZeroPosition: (() => {
        const zZero = dto.zZeroPosition as Post.IZZeroPosition;

        // Migration: fix invalid combinations for Rotary
        if (
          zZero === Post.IZZeroPosition.MATERIAL_SURFACE ||
          zZero === Post.IZZeroPosition.MACHINE_BED
        ) {
          return Post.IZZeroPosition.CYLINDER_SURFACE; // default for Rotary
        }

        return zZero || Post.IZZeroPosition.CYLINDER_SURFACE;
      })(),
      xyDatumPosition: dto.xyDatumPosition as Post.IXYDatumPosition,
      modelGroups: dto.modelGroups || [],
      toolSetter: dto.toolSetter as Post.IToolSetter,
      probeForZHeightCommand:
        dto.probeForZHeightCommand as Post.IProbeForZHeightCommand,
      models: modelAttachments,
      mediaFiles,
      thumbnail: thumbnailFile[0] || null,
      youtubeAttachments,
      isDimensionalMaterial: dto.isDimensionalMaterial ?? false,
      dimensionalMaterialLinks: dto.dimensionalMaterialLinks ?? [],
      supportedMachines: dto.supportedMachines,
      originalMachine: dto.originalMachine,
      licenceType: dto.licenceType as Post.ILicense,
    } as Post.IPostUploadData;
  }

  return {
    _id: dto._id,
    title: dto.title,
    description: dto.description,
    category: dto.category?._id || '',
    tags: dto.tags.map((tag) => tag._id),
    onefinityModel: dto.onefinityModel as Post.IOnefinityModel,
    onefinityController: dto.onefinityController as Post.IOnefinityController,
    onefinitySize: dto.onefinitySize as Post.IOnefinitySize,
    softwareUsed: (() => {
      if (Array.isArray(dto.softwareUsed)) {
        return dto.softwareUsed;
      }
      if (dto.softwareUsed) {
        return [dto.softwareUsed];
      }
      return [Object.values(Post.ISoftwareUsed)[0]];
    })() as Post.ISoftwareUsed[],
    materialUsed:
      (dto.materialUsed as unknown as Post.IMaterialUsed) ||
      Post.IWood.WOOD_HARD,
    difficulty: dto.difficulty ?? '',
    jobType: (dto.jobType as Post.IJobType) || Object.values(Post.IJobType)[0],
    flipDirection: (dto.flipDirection as Post.IFlipDirection) || '',
    units: (dto.units as Post.IUnits) || Object.values(Post.IUnits)[0],
    jobSizeHeight:
      dto.jobSize?.height !== undefined ? dto.jobSize.height : 0.25,
    jobSizeThickness:
      dto.jobSize?.thickness !== undefined ? dto.jobSize.thickness : 0.25,
    jobSizeWidth: dto.jobSize?.width !== undefined ? dto.jobSize.width : 0.25,
    zZeroPosition: (() => {
      const zZero = dto.zZeroPosition as Post.IZZeroPosition;

      // Migration: fix invalid combinations for non-Rotary
      if (
        zZero === Post.IZZeroPosition.CYLINDER_SURFACE ||
        zZero === Post.IZZeroPosition.CYLINDER_AXIS
      ) {
        return Post.IZZeroPosition.MATERIAL_SURFACE; // default for non-Rotary
      }

      return zZero || Object.values(Post.IZZeroPosition)[0];
    })(),
    xyDatumPosition:
      (dto.xyDatumPosition as Post.IXYDatumPosition) ||
      Object.values(Post.IXYDatumPosition)[0],
    modelGroups: dto.modelGroups || [],
    toolSetter:
      (dto.toolSetter as Post.IToolSetter) ||
      Object.values(Post.IToolSetter)[1],
    probeForZHeightCommand:
      (dto.probeForZHeightCommand as Post.IProbeForZHeightCommand) ||
      Object.values(Post.IProbeForZHeightCommand)[0],
    models: modelAttachments,
    mediaFiles,
    thumbnail: thumbnailFile[0] || null,
    youtubeAttachments,
    isDimensionalMaterial: dto.isDimensionalMaterial ?? false,
    dimensionalMaterialLinks: dto.dimensionalMaterialLinks ?? [],
    supportedMachines: dto.supportedMachines,
    originalMachine: dto.originalMachine,
    licenceType:
      (dto.licenceType as Post.ILicense) || Object.values(Post.ILicense)[0],
  } as Post.IPostUploadData;
}

/**
 * Converts data for sending to the backend
 */
export function convertToPostDetailData(
  dto: IProjectEditorState
): Post.IPostDataUploadRequest {
  const modelGroups = dto.modelGroups.map((group) => {
    // eslint-disable-next-line @typescript-eslint/naming-convention
    const { _id, ...groupRest } = group;
    return {
      ...groupRest,
      // Whitelist the LEAN tool shape so the wire payload never carries inline
      // specs (toolUnit/toolUsed/toolType/vBitDegree/tipRadius) or the resolved
      // `bit` read-model — regardless of which form steps were visited.
      toolData: group.toolData.map((tool) => {
        const isEndmill = tool.laserOrEndmill === Post.ILaserOrEndmill.ENDMILL;

        // Endmill keeps a valid in-range spindle speed; Laser never sends one.
        let spindleSpeed: number | undefined;
        if (isEndmill && tool.spindleSpeed != null) {
          const speed = Number(tool.spindleSpeed);
          if (
            !Number.isNaN(speed) &&
            speed >= SPINDLE_SPEED_MIN &&
            speed <= SPINDLE_SPEED_MAX
          ) {
            spindleSpeed = speed;
          }
        }

        const cleanedTool: {
          modelId: string;
          laserOrEndmill: Post.ILaserOrEndmill;
          bitId?: string;
          brandId?: string;
          spindleSpeed?: number;
        } = {
          modelId: tool.modelId,
          laserOrEndmill: tool.laserOrEndmill,
        };

        // bitId references a catalog bit: required for Endmill, optional for
        // Laser (which laser to use). Send it for either kind when present —
        // gating on isEndmill here silently dropped the selected laser's bit
        // from the wire payload, so laser rows saved with no bit.
        if (tool.bitId) {
          cleanedTool.bitId = tool.bitId;
        }
        // brandId records which of the bit's brands was picked (present only
        // once the backend accepts it; harmless when absent).
        if (tool.brandId) {
          cleanedTool.brandId = tool.brandId;
        }
        if (spindleSpeed !== undefined) {
          cleanedTool.spindleSpeed = spindleSpeed;
        }

        return cleanedTool;
      }),
    };
  });

  const isRotary = dto.jobType === Post.IJobType.ROTARY;
  const isDoubleSided = dto.jobType === Post.IJobType.DOUBLE_SIDED;

  const result = {
    title: dto.title,
    description: dto.description,
    category: dto.category,
    tags: dto.tags,
    softwareUsed: dto.softwareUsed,
    materialUsed: dto.materialUsed,
    difficulty: dto.difficulty,
    jobType: dto.jobType,
    ...(isDoubleSided && dto.flipDirection
      ? { flipDirection: dto.flipDirection }
      : {}),
    jobSize: isRotary
      ? {
          diameter: dto.jobSizeDiameter,
          length: dto.jobSizeLength,
        }
      : {
          height: dto.jobSizeHeight,
          width: dto.jobSizeWidth,
          thickness: dto.jobSizeThickness,
        },
    probeForZHeightCommand: dto.probeForZHeightCommand,
    toolSetter: dto.toolSetter,
    units: dto.units,
    xyDatumPosition: dto.xyDatumPosition,
    zZeroPosition: dto.zZeroPosition,
    youtubeAttachments: dto.youtubeAttachments,
    isDimensionalMaterial: dto.isDimensionalMaterial,
    dimensionalMaterialLinks: dto.dimensionalMaterialLinks,
    modelGroups,
    licenceType: dto.licenceType,
  };

  return Object.fromEntries(
    Object.entries(result).filter(
      // eslint-disable-next-line @typescript-eslint/no-unused-vars, no-unused-vars
      ([key, value]) =>
        value !== undefined &&
        value !== null &&
        (typeof value !== 'string' || value.trim() !== '') &&
        !(
          Array.isArray(value) &&
          value.length === 0 &&
          key !== 'supportedMachines' &&
          key !== 'youtubeAttachments' &&
          key !== 'dimensionalMaterialLinks'
        )
    )
  ) as unknown as Post.IPostDataUploadRequest;
}

// ---------------------------------------------------------------------------
// Profile feed (getPostsByUsername) pagination helpers
// ---------------------------------------------------------------------------

/** Default page size for the paginated profile feed. */
export const POSTS_PAGE_LIMIT = 24;

/** Sort options offered on the profile feed. */
export const SORT_OPTIONS: { value: Post.IPostsSort; label: string }[] = [
  { value: 'updatedAt:desc', label: 'Recently updated' },
  { value: 'createdAt:desc', label: 'Recently created' },
];

/**
 * Drop empty (`undefined` / `null` / `''`) entries from a query-param object so
 * absent filters (cursor / search / sort / visibility) aren't sent as blank
 * params. Keeps the `query` builder itself branch-free.
 */
export const compactParams = (
  params: Record<string, string | number | undefined | null>
): Record<string, string | number> =>
  Object.fromEntries(
    Object.entries(params).filter(
      ([, value]) => value !== undefined && value !== null && value !== ''
    )
  ) as Record<string, string | number>;

// ---------------------------------------------------------------------------
// Unified profile feed (getProfileFeed) — posts + project groups, keyset-paged.
// The cache key includes `visibility` so switching status tabs starts fresh.
// ---------------------------------------------------------------------------

/** Normalize the unified feed envelope, defaulting any missing fields. */
export const normalizeProfileFeedResponse = (
  response: Feed.IPaginatedFeedResponse
): Feed.IPaginatedFeedResponse => ({
  items: response.items ?? [],
  nextCursor: response.nextCursor ?? null,
  hasMore: response.hasMore ?? false,
});

/**
 * Cache key for the unified feed: everything EXCEPT the cursor, so successive
 * pages for the same username/search/sort/visibility merge into one list and
 * changing any of those starts a fresh first page.
 */
// fallow-ignore-next-line complexity
export const serializeProfileFeedArgs = ({
  queryArgs,
  endpointName,
}: {
  queryArgs: Feed.IGetProfileFeedArgs;
  endpointName: string;
}): string => {
  const { username, search, sort, visibility, category } = queryArgs;
  return `${endpointName}(${username}|${search ?? ''}|${sort ?? ''}|${
    visibility ?? ''
  }|${category ?? ''})`;
};

/** First page (no cursor) replaces the cache; later pages append `items`. */
export const mergeProfileFeed = (
  currentCache: Feed.IPaginatedFeedResponse,
  newItems: Feed.IPaginatedFeedResponse,
  { arg }: { arg: Feed.IGetProfileFeedArgs }
): Feed.IPaginatedFeedResponse => {
  if (!arg.cursor) {
    return newItems;
  }
  return {
    items: [...currentCache.items, ...newItems.items],
    nextCursor: newItems.nextCursor,
    hasMore: newItems.hasMore,
  };
};

/** Refetch whenever the cursor changes so each "load more" hits the network. */
export const profileFeedForceRefetch = ({
  currentArg,
  previousArg,
}: {
  currentArg: Feed.IGetProfileFeedArgs | undefined;
  previousArg: Feed.IGetProfileFeedArgs | undefined;
}): boolean => currentArg?.cursor !== previousArg?.cursor;
